import React, { useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS, ORDER_FLOW } from "../../lib/api";
import { play } from "../../lib/sound";
import { DashboardLayout, StatusBadge, StatCard } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { LayoutGrid, ClipboardList, UtensilsCrossed, Tag, Star, TrendingUp, Plus, Trash2, Pencil, Store, FolderTree, BadgeCheck, Zap, Target, Trophy, Gem, Award, Medal, Image as ImageIcon, Euro, Lock, CheckCircle2, Rocket, Percent, Users, Wallet, Bot, Briefcase, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import BusinessOnboarding from "../../components/BusinessOnboarding";
import ImageUpload from "../../components/ImageUpload";
import WalletStatement from "../../components/WalletStatement";
import BusinessAssistant from "../../components/BusinessAssistant";
import JobsPanel from "../../components/business/JobsPanel";
import ExperienceOptIn from "../../components/business/ExperienceOptIn";
import ServiceOfferingsPanel from "../../components/services/ServiceOfferingsPanel";
import ServiceBookingsPanel from "../../components/services/ServiceBookingsPanel";
import VisibilityPanel from "../../components/business/VisibilityPanel";
import BusinessAnalytics from "../../components/business/BusinessAnalytics";
import BookingPanel from "../../components/business/BookingPanel";
import CatalogPanel from "../../components/business/CatalogPanel";
import BusinessSubscriptionPanel from "../../components/business/BusinessSubscriptionPanel";
import AdvertisingPanel from "../../components/business/AdvertisingPanel";
import { CalendarDays, Package } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const NAV = [
  { key: "orders", label: "Ordini", path: "/business", icon: ClipboardList },
  { key: "assistant", label: "Assistente IA", path: "/business/assistant", icon: Bot },
  { key: "lavoro", label: "Lavoro", path: "/business/lavoro", icon: Briefcase },
  { key: "experiences", label: "Esperienze AI", path: "/business/experiences", icon: Sparkles },
  { key: "servizi", label: "Servizi ed Esperienze", path: "/business/servizi", icon: Sparkles },
  { key: "prenotazioni", label: "Prenotazioni servizi", path: "/business/prenotazioni", icon: Sparkles },
  { key: "catalog", label: "Catalogo", path: "/business/catalog", icon: Package },
  { key: "menu", label: "Menu", path: "/business/menu", icon: UtensilsCrossed },
  { key: "categories", label: "Categorie", path: "/business/categories", icon: FolderTree },
  { key: "coupons", label: "Coupon", path: "/business/coupons", icon: Tag },
  { key: "offer", label: "Offerta lampo", path: "/business/offer", icon: Zap },
  { key: "missions", label: "Missioni", path: "/business/missions", icon: Target },
  { key: "livelli", label: "Livelli & Rewards", path: "/app/livelli", icon: Award },
  { key: "reviews", label: "Recensioni", path: "/business/reviews", icon: Star },
  { key: "stats", label: "Statistiche", path: "/business/stats", icon: TrendingUp },
  { key: "analytics", label: "Business Intelligence", path: "/business/analytics", icon: LayoutGrid },
  { key: "booking", label: "Prenotazioni", path: "/business/booking", icon: CalendarDays },
  { key: "visibility", label: "Visibilità", path: "/business/visibility", icon: Rocket },
  { key: "subscription", label: "Abbonamento", path: "/business/subscription", icon: Award },
  { key: "advertising", label: "Pubblicità", path: "/business/advertising", icon: Rocket },
  { key: "wallet", label: "Portafoglio", path: "/business/wallet", icon: Wallet },
  { key: "profile", label: "Profilo attività", path: "/business/profile", icon: Store },
];

function Orders() {
  const [orders, setOrders] = useState([]);
  const prevCount = useRef(null);
  const load = async () => {
    const { data } = await api.get("/orders");
    const active = data.filter((o) => !["consegnato", "annullato"].includes(o.status)).length;
    if (prevCount.current !== null && active > prevCount.current) play("notification");
    prevCount.current = active;
    setOrders(data);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const setStatus = async (id, status, prep) => {
    try { await api.put(`/orders/${id}/status`, { status, prep_time: prep }); load(); toast.success("Stato aggiornato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`biz-order-${o.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div><span className="font-semibold">#{o.id.slice(-5)}</span> · {o.customer_name} · <span className="text-zinc-400">{o.fulfillment}</span></div>
            <StatusBadge status={o.status} />
          </div>
          <div className="text-sm text-zinc-400 mb-3">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")} — <span className="text-[#00E676] font-semibold">€{o.total.toFixed(2)}</span></div>
          {o.notes && <div className="text-xs text-zinc-500 mb-3">Note: {o.notes}</div>}
          <div className="flex flex-wrap gap-2">
            {o.status === "in_attesa" && (
              <>
                <Button size="sm" data-testid={`accept-${o.id}`} onClick={() => setStatus(o.id, "accettato", 25)} className="bg-[#00E676] text-black rounded-md">Accetta</Button>
                <Button size="sm" variant="destructive" data-testid={`reject-${o.id}`} onClick={() => setStatus(o.id, "annullato")}>Rifiuta</Button>
              </>
            )}
            {["accettato","in_preparazione","pronto"].includes(o.status) && (
              <Select onValueChange={(v) => setStatus(o.id, v)}>
                <SelectTrigger data-testid={`status-select-${o.id}`} className="w-52 bg-[#0A0A0C] border-white/10"><SelectValue placeholder="Cambia stato" /></SelectTrigger>
                <SelectContent>{["accettato","in_preparazione","pronto"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      ))}
      {orders.length === 0 && <div className="text-zinc-500 py-16 text-center">Nessun ordine.</div>}
    </div>
  );
}

function Menu() {
  const [products, setProducts] = useState([]);
  const empty = { name: "", description: "", price: "", category: "", image: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => { const { data } = await api.get("/business/me"); setProducts(data.products); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ name: p.name, description: p.description || "", price: String(p.price), category: p.category, image: p.image || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.category || form.price === "") { toast.error("Compila nome, categoria e prezzo"); return; }
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editId) await api.put(`/business/products/${editId}`, payload);
      else await api.post("/business/products", payload);
      setOpen(false); setForm(empty); setEditId(null); load();
      toast.success(editId ? "Prodotto aggiornato" : "Prodotto aggiunto");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAvail = async (p) => { await api.put(`/business/products/${p.id}`, { available: !p.available }); load(); };
  const del = async (id) => { if (!window.confirm("Eliminare il prodotto?")) return; await api.delete(`/business/products/${id}`); load(); toast.success("Prodotto eliminato"); };
  const cats = [...new Set(products.map((p) => p.category))];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-product-btn" onClick={openNew} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Nuovo prodotto</Button></DialogTrigger>
          <DialogContent className="bg-[#1C1C1F] border-white/10">
            <DialogHeader><DialogTitle>{editId ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="prod-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Categoria</Label><Input data-testid="prod-category" list="cat-list" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /><datalist id="cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist></div>
              <div><Label>Prezzo (€)</Label><Input data-testid="prod-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Immagine prodotto</Label><ImageUpload testid="prod-image" folder="products" value={form.image} onChange={(url) => setForm({ ...form, image: url })} /></div>
              <div><Label>Descrizione</Label><Textarea data-testid="prod-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <Button data-testid="save-product" onClick={save} className="bg-[#00E676] text-black rounded-md w-full">Salva</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {cats.map((c) => (
        <div key={c} className="mb-6">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{c}</h3>
          <div className="space-y-2">
            {products.filter((p) => p.category === c).map((p) => (
              <div key={p.id} data-testid={`menu-item-${p.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
                <div><div className="font-medium">{p.name}</div><div className="text-sm text-[#00E676]">€{p.price.toFixed(2)}</div></div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant={p.available ? "secondary" : "destructive"} data-testid={`toggle-avail-${p.id}`} onClick={() => toggleAvail(p)}>{p.available ? "Disponibile" : "Esaurito"}</Button>
                  <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="p-2 text-zinc-300 hover:bg-white/5 rounded"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-product-${p.id}`} onClick={() => del(p.id)} className="p-2 text-red-400 hover:bg-white/5 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {products.length === 0 && <div className="text-zinc-500 py-16 text-center">Nessun prodotto. Aggiungi il primo!</div>}
    </div>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const load = async () => { const { data } = await api.get("/business/categories"); setCats(data); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/business/categories", { name }); setName(""); load(); toast.success("Categoria creata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rename = async (id) => {
    try { await api.put(`/business/categories/${id}`, { name: editName }); setEditId(null); load(); toast.success("Categoria rinominata (prodotti aggiornati)"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/business/categories/${id}`); load(); toast.success("Categoria eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-xl">
      <div className="flex gap-2 mb-6">
        <Input data-testid="new-category" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nuova categoria…" className="bg-[#1C1C1F] border-white/10" />
        <Button data-testid="add-category" onClick={add} className="bg-[#00E676] text-black rounded-md"><Plus className="h-4 w-4 mr-1" /> Crea</Button>
      </div>
      <div className="space-y-2">
        {cats.map((c) => (
          <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center justify-between bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
            {editId === c.id ? (
              <Input data-testid={`edit-category-input-${c.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} className="bg-[#0A0A0C] border-white/10 mr-2" />
            ) : (
              <span className="font-medium">{c.name}</span>
            )}
            <div className="flex items-center gap-2">
              {editId === c.id ? (
                <Button size="sm" data-testid={`save-category-${c.id}`} onClick={() => rename(c.id)} className="bg-[#00E676] text-black rounded-md">Salva</Button>
              ) : (
                <button data-testid={`rename-category-${c.id}`} onClick={() => { setEditId(c.id); setEditName(c.name); }} className="p-2 text-zinc-300 hover:bg-white/5 rounded"><Pencil className="h-4 w-4" /></button>
              )}
              <button data-testid={`del-category-${c.id}`} onClick={() => del(c.id)} className="p-2 text-red-400 hover:bg-white/5 rounded"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {cats.length === 0 && <div className="text-zinc-500 py-8 text-center">Nessuna categoria.</div>}
      </div>
    </div>
  );
}

function Profile() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/business/me").then(({ data }) => setForm(data.business)); }, []);
  const save = async () => {
    try {
      await api.put("/business/me", { name: form.name, description: form.description, address: form.address, phone: form.phone, zone: form.zone, image: form.image, prep_time: parseInt(form.prep_time) || 25 });
      toast.success("Profilo attività aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="max-w-2xl bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4">
      <div><Label>Nome attività</Label><Input data-testid="biz-name" value={form.name} onChange={set("name")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      <div><Label>Descrizione</Label><Textarea data-testid="biz-desc" value={form.description || ""} onChange={set("description")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Indirizzo</Label><Input data-testid="biz-address" value={form.address || ""} onChange={set("address")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><Label>Telefono</Label><Input data-testid="biz-phone" value={form.phone || ""} onChange={set("phone")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Zona</Label><Input data-testid="biz-zone" value={form.zone || ""} onChange={set("zone")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><Label>Tempo preparazione (min)</Label><Input data-testid="biz-prep" type="number" value={form.prep_time || 25} onChange={set("prep_time")} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      </div>
      <div><Label>Immagine copertina</Label><div className="mt-1"><ImageUpload testid="biz-image" folder="businesses" value={form.image || ""} onChange={(url) => setForm({ ...form, image: url })} /></div></div>
      <Button data-testid="save-biz" onClick={save} className="bg-[#00E676] text-black rounded-md">Salva modifiche</Button>
    </div>
  );
}

function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_percent: "", discount_amount: "" });
  const load = async () => { const { data } = await api.get("/coupons"); setCoupons(data); };
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/coupons", { code: form.code, discount_percent: parseFloat(form.discount_percent) || 0, discount_amount: parseFloat(form.discount_amount) || 0, active: true }); setForm({ code: "", discount_percent: "", discount_amount: "" }); load(); toast.success("Coupon creato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare il coupon "${c.code}"? L'operazione è definitiva.`)) return;
    try { await api.delete(`/coupons/${c.id}`); toast.success("Coupon eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5 mb-6 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div><Label>Codice</Label><Input data-testid="coupon-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><Label>Sconto %</Label><Input data-testid="coupon-percent" type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><Label>Sconto €</Label><Input data-testid="coupon-amount" type="number" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <Button data-testid="create-coupon" onClick={add} className="bg-[#00E676] text-black rounded-md">Crea coupon</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-card-${c.id}`} className="bg-[#1C1C1F] border border-dashed border-[#00E676]/40 rounded-lg p-4 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display font-bold text-lg text-[#00E676]">{c.code}</div>
              <div className="text-sm text-zinc-400">{c.discount_percent ? `${c.discount_percent}% di sconto` : `€${c.discount_amount} di sconto`}</div>
            </div>
            <button data-testid={`delete-coupon-${c.id}`} onClick={() => remove(c)} title="Elimina coupon"
              className="p-2 text-red-400 hover:bg-red-500/10 rounded shrink-0"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reviews() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/business/reviews").then(({ data }) => setReviews(data)); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {reviews.map((r) => (
        <div key={r.id} className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
          <div className="flex justify-between"><span className="font-medium">{r.customer_name}</span><span className="flex items-center gap-0.5 text-[#00E676]"><Star className="h-3 w-3 fill-[#00E676]" /> {r.rating}</span></div>
          <p className="text-sm text-zinc-400 mt-1">{r.comment}</p>
        </div>
      ))}
      {reviews.length === 0 && <div className="text-zinc-500">Nessuna recensione.</div>}
    </div>
  );
}

function Stats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/business/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-rev-today" label="Vendite oggi" value={`€${s.revenue_today}`} accent icon={TrendingUp} />
        <StatCard testid="stat-rev-week" label="Vendite settimana" value={`€${s.revenue_week}`} icon={TrendingUp} />
        <StatCard testid="stat-rev-month" label="Vendite mese" value={`€${s.revenue_month}`} icon={TrendingUp} />
        <StatCard testid="stat-orders" label="Ordini totali" value={s.orders_total} icon={ClipboardList} />
      </div>
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold mb-4">Ultimi 7 giorni</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={s.daily}>
            <XAxis dataKey="day" stroke="#71717a" fontSize={12} /><YAxis stroke="#71717a" fontSize={12} />
            <Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
            <Bar dataKey="revenue" fill="#00E676" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function FlashOffer() {
  const [offer, setOffer] = useState({ active: false, title: "", until: "" });
  useEffect(() => { api.get("/business/me").then(({ data }) => { const o = data.business.map_offer; if (o) setOffer({ active: !!o.active, title: o.title || "", until: (o.until || "").slice(0, 10) }); }); }, []);
  const save = async () => {
    try {
      await api.put("/business/offer", { active: offer.active, title: offer.title, until: offer.until ? new Date(offer.until).toISOString() : null });
      toast.success(offer.active ? "Offerta pubblicata sulla mappa" : "Offerta disattivata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-xl bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2"><Zap className="h-5 w-5 text-[#FFD700]" /><h3 className="font-semibold">Offerta lampo sulla mappa</h3></div>
      <p className="text-sm text-zinc-400">Quando attiva, la tua attività mostra un alone luminoso e un badge "Offerta" sulla mappa di esplorazione pubblica, per attirare i clienti.</p>
      <div><Label>Titolo offerta</Label><Input data-testid="offer-title" value={offer.title} onChange={(e) => setOffer({ ...offer, title: e.target.value })} placeholder="Es. -20% oggi su tutte le pizze" className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      <div><Label>Valida fino al (facoltativo)</Label><Input data-testid="offer-until" type="date" value={offer.until} onChange={(e) => setOffer({ ...offer, until: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
      <label className="flex items-center gap-2 text-sm cursor-pointer"><input data-testid="offer-active" type="checkbox" checked={offer.active} onChange={(e) => setOffer({ ...offer, active: e.target.checked })} className="h-4 w-4 accent-[#00E676]" /> Offerta attiva sulla mappa</label>
      <Button data-testid="offer-save" onClick={save} className="bg-[#00E676] text-black rounded-md">Salva offerta</Button>
    </div>
  );
}

const MISSION_ICONS = { store: Store, "utensils-crossed": UtensilsCrossed, image: ImageIcon, zap: Zap, "trending-up": TrendingUp, euro: Euro, star: Star, tag: Tag, users: Users, target: Target };
const TIER_COLORS = { bronze: "#CD7F32", silver: "#C7CAD1", gold: "#FFD700", platinum: "#7DF9FF" };
const LEVEL_ICONS = { bronze: Medal, silver: Award, gold: Trophy, platinum: Gem };
const CAT_ORDER = ["Avvio", "Visibilità", "Vendite", "Qualità", "Promozione", "Community"];

function MissionCard({ m }) {
  const Icon = MISSION_ICONS[m.icon] || Target;
  const pct = Math.min(100, Math.round((m.current_value / m.max_threshold) * 100));
  const reachedColor = m.tier_reached ? TIER_COLORS[m.tier_reached] : "#00E676";
  return (
    <div data-testid={`mission-${m.code}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors">
      <div className="flex items-start gap-3 mb-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${reachedColor}22`, border: `1px solid ${reachedColor}55` }}>
          <Icon className="h-5 w-5" style={{ color: reachedColor }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold truncate">{m.title}</h4>
            {m.completed && <CheckCircle2 className="h-4 w-4 text-[#00E676] shrink-0" />}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5 leading-snug">{m.description}</p>
        </div>
      </div>
      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="text-zinc-400">Progresso: <b className="text-white" data-testid={`mission-value-${m.code}`}>{m.current_value}</b> / {m.max_threshold}</span>
        {m.tier_reached && <span className="font-semibold uppercase tracking-wide" style={{ color: reachedColor }}>{m.tier_reached}</span>}
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${reachedColor}, #00E676)` }} />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {m.tiers.map((t) => {
          const done = m.current_value >= t.threshold;
          const c = TIER_COLORS[t.key];
          return (
            <div key={t.key} className="text-[10px] px-2 py-1 rounded-md flex items-center gap-1" style={{ background: done ? `${c}22` : "rgba(255,255,255,0.03)", border: `1px solid ${done ? c + "66" : "rgba(255,255,255,0.08)"}`, color: done ? c : "#a1a1aa" }}>
              {done ? <CheckCircle2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
              <span>{t.label} · {t.threshold}</span>
              <span className="opacity-70">+{t.reward.credits}c</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Missions() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/business/missions").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="text-zinc-500 py-16 text-center">Caricamento missioni…</div>;
  const lv = data.level;
  const LvIcon = LEVEL_ICONS[lv.level] || Trophy;
  const lvColor = TIER_COLORS[lv.level] || "#00E676";
  const span = lv.next_level_xp != null ? lv.next_level_xp - lv.current_level_xp : 1;
  const xpPct = lv.next_level_xp != null ? Math.min(100, Math.round(((lv.xp - lv.current_level_xp) / span) * 100)) : 100;
  const cats = CAT_ORDER.filter((c) => data.missions.some((m) => m.category === c));
  return (
    <div className="space-y-8">
      <div data-testid="business-level-card" className="relative overflow-hidden rounded-2xl p-6 border border-white/10" style={{ background: `radial-gradient(120% 140% at 0% 0%, ${lvColor}22, #131316 60%)` }}>
        <div className="flex flex-wrap items-center gap-5">
          <div className="h-16 w-16 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${lvColor}22`, border: `1px solid ${lvColor}66`, boxShadow: `0 0 30px ${lvColor}33` }}>
            <LvIcon className="h-8 w-8" style={{ color: lvColor }} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Livello attività</div>
            <div className="text-2xl font-display font-bold" style={{ color: lvColor }} data-testid="business-level-label">{lv.level_label}</div>
            {lv.next_level ? (
              <div className="mt-2">
                <div className="h-2 rounded-full bg-white/10 overflow-hidden max-w-sm">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${xpPct}%`, background: `linear-gradient(90deg, ${lvColor}, #00E676)` }} />
                </div>
                <div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · prossimo livello <b className="text-zinc-300">{lv.next_level}</b> a {lv.next_level_xp} XP</div>
              </div>
            ) : <div className="text-xs text-zinc-500 mt-1">{lv.xp} XP · livello massimo raggiunto</div>}
          </div>
          <div className="flex gap-3">
            <div className="text-center px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-1 text-[#00E676] font-bold text-xl" data-testid="business-credits"><Gem className="h-4 w-4" /> {lv.credits}</div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Crediti</div>
            </div>
            <div className="text-center px-4 py-2 rounded-xl bg-white/5 border border-white/10">
              <div className="text-xl font-bold" data-testid="business-badges-count">{lv.badges.length}</div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Badge</div>
            </div>
          </div>
        </div>
        {(lv.map_boost?.active || lv.commission_discount) && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="business-active-rewards">
            {lv.map_boost?.active && <span className="text-xs px-3 py-1 rounded-full flex items-center gap-1.5" style={{ background: "#7DF9FF22", border: "1px solid #7DF9FF55", color: "#7DF9FF" }}><Rocket className="h-3.5 w-3.5" /> Boost visibilità attivo</span>}
            {lv.commission_discount && <span className="text-xs px-3 py-1 rounded-full flex items-center gap-1.5" style={{ background: "#00E67622", border: "1px solid #00E67655", color: "#00E676" }}><Percent className="h-3.5 w-3.5" /> Sconto commissione -{Math.round((lv.commission_discount.discount || 0) * 100 * 10) / 10}%</span>}
          </div>
        )}
      </div>

      {cats.map((cat) => (
        <div key={cat}>
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">{cat}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.missions.filter((m) => m.category === cat).map((m) => <MissionCard key={m.code} m={m} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function CelebrationWatcher() {
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/business/rewards/unseen");
        if (data && data.length) {
          confetti({ particleCount: 130, spread: 80, origin: { y: 0.35 }, colors: ["#00E676", "#FFD700", "#7DF9FF"] });
          data.slice(0, 3).forEach((r) => toast.success(`Missione «${r.mission_title}» — ${r.tier_label} completato! +${r.reward?.credits || 0} crediti`, { duration: 7000 }));
          await api.post("/business/rewards/seen");
        }
      } catch { /* silenzioso */ }
    };
    check();
    const t = setInterval(check, 20000);
    return () => clearInterval(t);
  }, []);
  return null;
}

export default function BusinessDashboard() {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/business/me"); setBusiness(data.business); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <DashboardLayout title="Dashboard Attività" nav={[]}><div className="text-zinc-500 py-16 text-center">Caricamento…</div></DashboardLayout>;

  if (business && business.status !== "approved") {
    const onbNav = [{ key: "onboarding", label: "Verifica attività", path: "/business", icon: BadgeCheck }];
    return (
      <DashboardLayout title="Dashboard Attività" nav={onbNav}>
        <Routes>
          <Route path="*" element={<BusinessOnboarding business={business} onUpdate={load} />} />
        </Routes>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard Attività" nav={NAV}>
      <CelebrationWatcher />
      <Routes>
        <Route index element={<Orders />} />
        <Route path="assistant" element={<BusinessAssistant />} />
        <Route path="lavoro" element={<JobsPanel />} />
        <Route path="experiences" element={<ExperienceOptIn />} />
        <Route path="servizi" element={<ServiceOfferingsPanel />} />
        <Route path="prenotazioni" element={<ServiceBookingsPanel />} />
        <Route path="menu" element={<Menu />} />
        <Route path="catalog" element={<CatalogPanel />} />
        <Route path="categories" element={<Categories />} />
        <Route path="coupons" element={<Coupons />} />
        <Route path="offer" element={<FlashOffer />} />
        <Route path="missions" element={<Missions />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="stats" element={<Stats />} />
        <Route path="analytics" element={<BusinessAnalytics />} />
        <Route path="booking" element={<BookingPanel />} />
        <Route path="visibility" element={<VisibilityPanel />} />
        <Route path="subscription" element={<BusinessSubscriptionPanel />} />
        <Route path="advertising" element={<AdvertisingPanel />} />
        <Route path="wallet" element={<WalletStatement />} />
        <Route path="profile" element={<Profile />} />
      </Routes>
    </DashboardLayout>
  );
}
